import React from "react";
import { INavBarItem } from "../../interfaces/others/INavBarItem";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { IRoute } from "../../interfaces/others/IRoute";
import { useSelector } from "react-redux";
import { RootState } from "../../store/index";

const NavBarItem: React.FC<
  INavBarItem & { childrenRoutes?: IRoute[]; isNew?: boolean }
> = ({
  active,
  name,
  to,
  activeKey,
  icon,
  childrenRoutes,
  isNew,
  disabled,
}) => {
  const { user, partner } = useSelector((state: RootState) => state.auth);
  const { isDirectBoss, bossId } = useSelector(
    (state: RootState) => state.directBoss as any
  );

  const isVacationsResponsible = partner?.isVacationsResponsible === true;

  const canAccessRoute = (validRoles: string[]) => {
    if (validRoles.includes(user.rol)) {
      return true;
    }

    if (user.rol === "Trabajador") {
      if (isDirectBoss) {
        const directBossRoutes = ["incapacidades", "permisos", "vacaciones", "incidencias"];
        const isDirectBossRoute = directBossRoutes.some(
          (route) => to.includes(route) || activeKey.includes(route)
        );
        return isDirectBossRoute || validRoles.includes("Trabajador");
      }

      if (isVacationsResponsible && to.includes("vacaciones")) {
        return true;
      }

      return validRoles.includes("Trabajador");
    }

    return false;
  };

  const filteredChildren = childrenRoutes?.filter((child) =>
    canAccessRoute(child.validRoles)
  );

  const hasChildren =
    Array.isArray(filteredChildren) && filteredChildren.length > 0;

  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement | null>(null);
  const timeoutRef = React.useRef<number | null>(null);

  const computedTo = React.useMemo(() => {
    if (user.rol === "Trabajador" && isDirectBoss && (bossId || partner?.id)) {
      const directBossRoots = ["/incapacidades", "/permisos", "/vacaciones", "/incidencias"];
      if (directBossRoots.includes(to)) {
        return `${to}/${bossId ?? partner?.id}`;
      }
    }

    if (
      user.rol === "Trabajador" &&
      isVacationsResponsible &&
      !isDirectBoss &&
      to.includes("vacaciones")
    ) {
      return "/vacaciones";
    }

    return to;
  }, [to, user.rol, isDirectBoss, isVacationsResponsible, partner?.id, bossId]);

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = window.setTimeout(() => {
      setOpen(false);
    }, 200);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const canShowNavItem = () => {
    if (childrenRoutes) {
      return hasChildren;
    }
    if (user.rol.includes("Administrador") || user.rol.includes("Lider")) {
      return true;
    }

    if (user.rol === "Trabajador") {
      if (isDirectBoss) {
        const directBossRoutes = [
          "mi-expediente",
          "incapacidades",
          "permisos",
          "vacaciones",
          "incidencias",
        ];
        const canAccess = directBossRoutes.some(
          (route) => to.includes(route) || activeKey.includes(route)
        );

        return canAccess;
      }

      if (
        isVacationsResponsible &&
        (to.includes("vacaciones") || activeKey.includes("vacaciones"))
      ) {
        return true;
      }

      const canAccess =
        to.includes("mi-expediente") || activeKey.includes("expediente");

      return canAccess;
    }

    return false;
  };

  if (!canShowNavItem()) {
    return null;
  }

  if (hasChildren) {
    return (
      <div
        ref={wrapperRef}
        className={`nav-item dropdown${open ? " show" : ""}`}
        onMouseEnter={disabled ? undefined : handleMouseEnter}
        onMouseLeave={disabled ? undefined : handleMouseLeave}
      >
        <Link
          to={computedTo}
          className={`${
            active.includes(activeKey)
              ? "nav-link active dropdown-toggle"
              : "nav-link dropdown-toggle"
          }${disabled ? " disabled" : ""}`}
          aria-expanded={open}
          aria-haspopup="true"
          id={`dropdown-${activeKey}`}
          style={{
            textDecoration: "none",
            cursor: disabled ? "not-allowed" : "pointer",
            opacity: disabled ? 0.6 : 1,
          }}
          onClick={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            setOpen((prev) => !prev);
            navigate(computedTo);
          }}
        >
          <i className={`fa-solid fa-${icon} me-1`}></i>
          {name}
          {filteredChildren?.some((child) => child.isNew) && (
            <span className="new-badge">New</span>
          )}
        </Link>
        <ul
          className={`dropdown-menu${open ? " show" : ""}`}
          aria-labelledby={`dropdown-${activeKey}`}
        >
          {filteredChildren!.map((child) => (
            <li key={child.to}>
              <Link
                to={child.to}
                className="dropdown-item"
                style={{ cursor: "pointer" }}
                onClick={() => setOpen(false)}
              >
                <i className={`fa-solid fa-${child.icon} me-2`}></i>
                {child.name}
                {child.isNew && <span className="new-badge">New</span>}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <Link
      to={computedTo}
      style={{ textDecoration: "none" }}
      onClick={(e) => {
        if (disabled) {
          e.preventDefault();
          return;
        }
      }}
    >
      <a
        className={`${
          active.includes(activeKey) ? "nav-link active" : "nav-link"
        }${disabled ? " disabled" : ""}`}
        style={{
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.6 : 1,
        }}
      >
        <i className={`fa-solid fa-${icon} me-1`}></i>
        {name}
        {isNew && <span className="new-badge">New</span>}
      </a>
    </Link>
  );
};

export default NavBarItem;
